import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, Leaf, LogOut, Clock } from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { listIdentifyQueue } from "@/lib/offlineCache";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Home() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();
  const [showSignOut, setShowSignOut] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const touchStart = useRef(null);

  // Poll the background retry queue so the badge reflects the current number
  // of photos still being retried after a transient Cloudflare/timeout failure.
  // Refreshes on: mount, tab visibility change, online event, and every 20s.
  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    const refresh = async () => {
      const entries = await listIdentifyQueue(user.user_id);
      if (!cancelled) setRetryCount((entries || []).length);
    };
    refresh();
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    const interval = setInterval(refresh, 20_000);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("online", refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("online", refresh);
    };
  }, [user?.user_id]);

  // Push a sentinel state so the user has something to "go back" from.
  useEffect(() => {
    window.history.pushState({ home: true }, "");
    const onPop = () => {
      // Re-add sentinel and prompt
      window.history.pushState({ home: true }, "");
      setShowSignOut(true);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Swipe-left detection (touch)
  const onTouchStart = (e) => {
    const t0 = e.touches?.[0];
    if (!t0) return;
    touchStart.current = { x: t0.clientX, y: t0.clientY, t: Date.now() };
  };
  const onTouchEnd = (e) => {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start) return;
    const t1 = e.changedTouches?.[0];
    if (!t1) return;
    const dx = t1.clientX - start.x;
    const dy = t1.clientY - start.y;
    const dt = Date.now() - start.t;
    // Strong left swipe: dx <= -80, |dy| < 60, under 800ms
    if (dx <= -80 && Math.abs(dy) < 60 && dt < 800) {
      setShowSignOut(true);
    }
  };

  const confirmSignOut = async () => {
    setShowSignOut(false);
    await signOut();
    navigate("/login", { replace: true });
  };

  const firstName = user?.name?.split(" ")[0] || "";

  return (
    <AppShell hideBottomNav>
      <div
        className="relative mx-auto md:max-w-3xl"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="home-page"
      >
        {/* Hero — decorative image only (state + greeting live below in high-contrast text). */}
        <div className="mx-5 mt-2 rounded-3xl overflow-hidden relative aspect-[16/9] md:aspect-[21/9]">
          <img
            src="https://images.unsplash.com/photo-1597848212624-a19eb35e2651?auto=format&fit=crop&w=900&q=80"
            alt="Sunflower field — Helianthus annuus, native to North America"
            className="absolute inset-0 w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src =
                "https://images.unsplash.com/photo-1772602841938-2f07739e58fe?auto=format&fit=crop&w=900&q=80";
            }}
          />
        </div>

        {/* Greeting + purpose — leaf-ink on cream = AAA contrast. */}
        <div className="mx-5 mt-5">
          <h1
            className="font-display text-3xl leading-tight text-leaf-ink"
            data-testid="home-greeting"
          >
            {t("home.greeting", { name: firstName })}
          </h1>
          <p
            className="mt-2 text-sm text-leaf-mist leading-relaxed"
            data-testid="home-tagline"
          >
            {t("home.tagline", { state: user?.state || "" })}
          </p>
        </div>

        {/* Retry-queue badge — visible only when photos are still being
            retried in the background after transient identify failures. */}
        {retryCount > 0 && (
          <div className="mx-5 mt-4">
            <button
              type="button"
              onClick={() => navigate("/gallery?filter=needs_id")}
              data-testid="retry-queue-badge"
              className="w-full inline-flex items-center gap-2.5 rounded-2xl bg-[#FFF4DC] border border-[#E9C46A]/50 text-[#8a6a14] px-4 py-2.5 text-sm hover:bg-[#FFEEC5] transition text-left"
            >
              <Clock className="w-4 h-4 shrink-0" strokeWidth={1.75} aria-hidden />
              <span className="flex-1 font-medium">
                {retryCount === 1
                  ? t("home.retryQueueBadgeOne")
                  : t("home.retryQueueBadgeMany", { n: retryCount })}
              </span>
              <span className="text-[#8a6a14]/70 text-xs" aria-hidden>›</span>
            </button>
          </div>
        )}

        {/* CTAs — kept compact on tablet+ so they don't overwhelm the sunflower hero. */}
        <div className="px-5 mt-5 grid grid-cols-2 gap-3 md:max-w-xl md:mx-auto lg:max-w-2xl">
          <button
            onClick={() => navigate("/camera")}
            data-testid="home-identify-button"
            className="rounded-3xl bg-leaf hover:bg-leaf-deep text-white p-5 md:p-4 text-left flex flex-col gap-3 md:gap-2 active:scale-[0.98] transition shadow-botanical"
          >
            <Camera className="w-6 h-6 md:w-5 md:h-5" strokeWidth={1.5} />
            <span className="font-display text-2xl md:text-xl leading-tight">{t("home.ctaIdentify")}</span>
            <span className="text-[12px] md:text-[11px] opacity-90">{t("home.ctaIdentifySub")}</span>
          </button>
          <button
            onClick={() => navigate("/gallery")}
            data-testid="home-gallery-button"
            className="rounded-3xl bg-parchment text-leaf-ink p-5 md:p-4 text-left flex flex-col gap-3 md:gap-2 active:scale-[0.98] transition shadow-botanical hover:bg-[#EEE9DE]"
          >
            <Leaf className="w-6 h-6 md:w-5 md:h-5 text-leaf" strokeWidth={1.5} />
            <span className="font-display text-2xl md:text-xl leading-tight">{t("home.ctaGallery")}</span>
            <span className="text-[12px] md:text-[11px] text-leaf-mist">{t("home.ctaGallerySub")}</span>
          </button>
        </div>
      </div>

      <AlertDialog open={showSignOut} onOpenChange={setShowSignOut}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("home.signOutTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("home.signOutDesc")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="signout-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="signout-confirm"
              onClick={confirmSignOut}
              className="bg-rust hover:bg-rust/90"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> {t("home.signOutConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
